import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Card } from '@/components/ui/card'
import {
  Users,
  Building2,
  Bot,
  Workflow,
  Brain,
  CheckCircle,
  TrendingUp,
  type LucideIcon,
} from 'lucide-react'
import { adminService, type AdminDashboardStats } from '@/services/admin'
import type { AxiosError } from 'axios'

interface StatCard {
  id: string
  labelKey: string
  value: number | string
  icon: LucideIcon
  color: 'indigo' | 'emerald' | 'purple' | 'amber' | 'cyan' | 'pink'
  href?: string
}

const COLOR_CLASSES: Record<
  StatCard['color'],
  { bg: string; text: string; icon: string }
> = {
  indigo: {
    bg: 'bg-indigo-500/20',
    text: 'text-indigo-400',
    icon: 'text-indigo-400',
  },
  emerald: {
    bg: 'bg-emerald-500/20',
    text: 'text-emerald-400',
    icon: 'text-emerald-400',
  },
  purple: {
    bg: 'bg-purple-500/20',
    text: 'text-purple-400',
    icon: 'text-purple-400',
  },
  amber: {
    bg: 'bg-amber-500/20',
    text: 'text-amber-400',
    icon: 'text-amber-400',
  },
  cyan: {
    bg: 'bg-cyan-500/20',
    text: 'text-cyan-400',
    icon: 'text-cyan-400',
  },
  pink: {
    bg: 'bg-pink-500/20',
    text: 'text-pink-400',
    icon: 'text-pink-400',
  },
}

function StatCardSkeleton() {
  return (
    <Card variant="glass" className="p-6 animate-pulse">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="h-4 bg-gray-700/50 rounded w-24 mb-3" />
          <div className="h-8 bg-gray-700/50 rounded w-16 mb-2" />
          <div className="h-3 bg-gray-700/50 rounded w-20" />
        </div>
        <div className="w-12 h-12 rounded-xl bg-gray-700/50" />
      </div>
    </Card>
  )
}

export function AdminDashboardPage() {
  const { t } = useTranslation('admin')
  const navigate = useNavigate()
  const [stats, setStats] = useState<AdminDashboardStats | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    async function fetchDashboard() {
      setIsLoading(true)
      setError(null)
      try {
        const response = await adminService.getDashboard()
        setStats(response.stats)
      } catch (err) {
        const axiosError = err as AxiosError<{ detail: string }>
        setError(axiosError.response?.data?.detail || t('dashboard.error'))
      } finally {
        setIsLoading(false)
      }
    }

    fetchDashboard()
  }, [t])

  const statCards: StatCard[] = stats
    ? [
        {
          id: 'total_users',
          labelKey: 'dashboard.totalUsers',
          value: stats.total_users,
          icon: Users,
          color: 'indigo',
          href: '/admin/users',
        },
        {
          id: 'active_users',
          labelKey: 'dashboard.activeUsers',
          value: stats.active_users,
          icon: CheckCircle,
          color: 'emerald',
          href: '/admin/users',
        },
        {
          id: 'total_organizations',
          labelKey: 'dashboard.totalOrganizations',
          value: stats.total_organizations,
          icon: Building2,
          color: 'purple',
          href: '/admin/organizations',
        },
        {
          id: 'total_projects',
          labelKey: 'dashboard.totalProjects',
          value: stats.total_projects,
          icon: Workflow,
          color: 'amber',
        },
        {
          id: 'total_agents',
          labelKey: 'dashboard.totalAgents',
          value: stats.total_agents,
          icon: Bot,
          color: 'cyan',
        },
        {
          id: 'total_workflows',
          labelKey: 'dashboard.totalWorkflows',
          value: stats.total_workflows,
          icon: Workflow,
          color: 'pink',
        },
        {
          id: 'total_knowledge_bases',
          labelKey: 'dashboard.totalKnowledgeBases',
          value: stats.total_knowledge_bases,
          icon: Brain,
          color: 'emerald',
        },
      ]
    : []

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-xl bg-indigo-500/20 flex items-center justify-center">
          <TrendingUp className="w-6 h-6 text-indigo-400" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-100">{t('dashboard.title')}</h1>
          <p className="text-gray-400 text-sm">{t('dashboard.subtitle')}</p>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <Card variant="glass" className="p-6 bg-red-500/10 border-red-500/20">
          <p className="text-red-400">{error}</p>
        </Card>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {isLoading ? (
          <>
            {Array.from({ length: 8 }).map((_, index) => (
              <StatCardSkeleton key={index} />
            ))}
          </>
        ) : (
          statCards.map((stat) => {
            const colors = COLOR_CLASSES[stat.color]
            return (
              <Card
                key={stat.id}
                variant="glass"
                className={`p-6${stat.href ? ' cursor-pointer hover:border-indigo-500/50 transition-all' : ''}`}
                role={stat.href ? 'link' : undefined}
                tabIndex={stat.href ? 0 : undefined}
                onClick={stat.href ? () => navigate(stat.href as string) : undefined}
                onKeyDown={
                  stat.href
                    ? (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          navigate(stat.href as string)
                        }
                      }
                    : undefined
                }
              >
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-gray-400 text-sm font-medium">
                      {t(stat.labelKey)}
                    </p>
                    <p className="text-3xl font-bold mt-2 text-gray-100">
                      {stat.value.toLocaleString()}
                    </p>
                    <p className={`text-sm mt-1 ${colors.text}`}>
                      {t('dashboard.platformWide')}
                    </p>
                  </div>
                  <div
                    className={`w-12 h-12 rounded-xl flex items-center justify-center ${colors.bg}`}
                  >
                    <stat.icon className={`w-6 h-6 ${colors.icon}`} />
                  </div>
                </div>
              </Card>
            )
          })
        )}
      </div>

      {/* Recent Activity Section */}
      <Card variant="glass" className="p-6">
        <h2 className="text-lg font-semibold text-gray-100 mb-4">
          {t('dashboard.recentActivity')}
        </h2>
        <div className="text-center py-12">
          <p className="text-gray-500">{t('dashboard.noActivity')}</p>
        </div>
      </Card>
    </div>
  )
}
